import Promise from 'bluebird';
import _ from 'lodash';

import api from '../api';
import { converter } from '../core/converter';
import infoBoard from '../core/infoboard';
import worldMap from '../core/map';
import translate from '../core/tools/translate';
import templates from '../core/templates';
import environment from '../environment';
import { customScrollbar, isLayerEnabled } from '../shared';
import { setExportActionCss } from '../../utils';
import { selectMarker } from '../shared/map';
import { CONFIG } from '../../config';

const LayerControls = (name) => require('../controls').get(name);
const LayerInfo = (name) => require('../info').get(name);

const currentLayer = () => LayerControls('Greenfield').getLayer();
const isAuthorized = (permission, feature) => api.authorization.isAuthorized(permission, feature);

const name = 'Greenfield';

const _getContainer = () =>
    infoBoard.createContainer({
        id: 'greenfield',
        name: translate('Common.Greenfield')
    });

const _getImage = latlng =>
    new Promise((resolve) => {
        const images = {
            original: '#',
            thumbnail: `https://maps.googleapis.com/maps/api/streetview?size=${worldMap.getThumbImageSize()}&location=${latlng.lat},${latlng.lng}&sensor=false&key=${CONFIG.GOOGLE_MAPS_API_KEY}`
        };
        return resolve(images);
    });

const _getInfo = (feature, resultType) =>
    new Promise((resolve) =>
        api.greenfield.getGreenfield(feature.properties.Id, resultType).then(response => resolve(response))
    );

const _getContext = function () {
    return _.values(environment.get('CONFIG.FILTERS.POINTS_TYPES'))[0];
};

class GreenfieldInfo {
    static getInstance() {
        if (!GreenfieldInfo._instance) {
            GreenfieldInfo._instance = new GreenfieldInfo();
        }

        return GreenfieldInfo._instance;
    }

    init() {
    }

    _getFeatureLayer(feature) {
        return currentLayer().getLayer(feature.properties.Id);
    }

    _updateVoronoi(feature, layer) {
        if (worldMap.hasLayer(layer)) {
            return LayerControls('Voronoi').add([feature]);
        } else {
            return LayerControls('Voronoi').remove([feature.properties.Id]);
        }
    }

    create(features) {
        if (infoBoard.locked) {
            return;
        }

        const container = _getContainer();

        if (infoBoard.dataUnavailable({
            features,
            container,
            isEnabled: isLayerEnabled(name)
        })) {
            return;
        }

        const context = _getContext();

        const _entry = {};

        const template = $(templates('infoboard-content', {
            id: 'points-collapse',
            actions: isAuthorized('points-summary-export')
        }));
        const content = template.find('.panel-group');

        features.forEach(feature => {
            let item;
            let layer = this._getFeatureLayer(feature);

            const recalculeteArea = () => {
                LayerInfo('Summary').recalculate({
                    container: item.summary,
                    id: feature.properties.Id,
                    latlng: layer.getLatLng(),
                    value: feature.properties['RaioServiceAreaMt'],
                    layer: currentLayer(),
                    controls: LayerControls('Greenfield').getControls(),
                    update: true,
                    feature
                });
                $(item.btn.summary).tab('show');
            };

            const _actionsOnClick = value => {
                switch (value) {
                    case 'locate':
                        return worldMap.setView(layer.getLatLng(), 16);
                }
            };

            let options = {
                feature,
                name: name.toLocaleLowerCase(),
                originalName: name,
                container,
                controls: {
                    actions: {
                        edit: false,
                        remove: false,
                        recalculate: false,
                        callback: _actionsOnClick
                    },
                    info: true,
                    summary: false,
                    compare: false, // isAuthorized("#{context}-comparison-view")
                    hide: true
                },
            };

            item = infoBoard.createItem(options);
            if (!item) {
                return;
            }

            _entry[feature.properties.Id] = feature;

            if (item.btn.info) {
                item.btn.info.on('click', e => {
                    e.preventDefault();
                    const queue = [];
                    const coordinates = {
                        lat: feature.geometry.coordinates[1],
                        lng: feature.geometry.coordinates[0]
                    };
                    queue.push(_getInfo(feature, context));
                    queue.push(_getImage(coordinates));
                    Promise.all(queue).then(response => {
                        const data = response[0];
                        data.image = response[1];
                        data.name = feature.properties['Name'].toCapitalCase();
                        data.id = feature.properties['CodePI'];
                        LayerInfo('Resume').show(data, {
                            resultType: 'Greenfield',
                            visible: true,
                            title: translate('Common.PointInfo'),
                            latlng: coordinates,
                            callbackBack: () => {
                                infoBoard.showInfo('Greenfield', feature.properties.Id);
                                worldMap.getCurrent().fireEvent('click');
                            },
                        });
                    });

                    selectMarker(layer, feature, 'Greenfield', 'points');
                });
            }

            if (item.btn.summary) {
                item.btn.summary.on('click', function (e) {
                    e.preventDefault();
                    if (feature.properties.PossuiServiceArea) {
                        options = {
                            // container: item.summary
                            id: feature.properties.Id,
                            latlng: layer.getLatLng(),
                            type: 7,
                            context: 'points',
                            layer: currentLayer(),
                            title: feature.properties.Nome,
                            value: feature.properties.RaioServiceAreaMt,
                            visible: true
                        };
                        // scrollTo: true
                        LayerInfo('Summary').create(options);
                    } else if (isAuthorized('points-service-area-generate')) {
                        recalculeteArea();
                    }
                });
            }

            if (item.btn.hide) {
                // toggle icon if the current feature was target as hidden
                if (currentLayer().isHidden(feature.properties.Id)) {
                    $(item.btn.hide).find('i').toggleClass('fa-eye fa-eye-slash');
                    this._updateVoronoi(feature, layer);
                }

                item.btn.hide.on('click', () => {
                    LayerControls('Greenfield').toggleVisibility(feature.properties.Id, layer);
                    if (!layer) {
                        layer = this._getFeatureLayer(feature);
                    }

                    this._updateVoronoi(feature, layer);
                });
            }

            // item.title.on('mouseover', () => LayerControls('Greenfield').highlight(layer));

            // item.title.on 'click', (e) ->
            //  currentLayer().panTo(layer.getLatLng())

            $(content).append(item.layout);
        });

        // give the DOM a break
        $(container).detach().append(template).appendTo('#infoboard-content');

        // add context panel
        const actions = infoBoard.createActions({
            export: {
                value: isAuthorized('points-summary-export'),
                label: translate('Action.ExportPoints')
            },
            compare: {
                value: false // isAuthorized("#{context}-comparison-view")
            }
        }, 'Greenfield');

        if (actions.export || actions.compare) {
            actions.layout.appendTo(container);
        }

        if (actions.compare) {
            $(actions.compare).on('click', e => {
                e.preventDefault();
                if (LayerInfo('Compare').getStatus()) {
                    return;
                }

                infoBoard.enableCompare(container);
                if (LayerControls('Dealerships').check()) {
                    infoBoard.enableCompare('#dealerships-tab');
                }

                LayerInfo('Compare').create();
            });

            // compare points
            if (LayerInfo('Compare').getStatus()) {
                infoBoard.enableCompare(container, context);
            }
        }

        if (actions.export) {
            setExportActionCss();
            $(actions.export).on('click', e => {
                e.stopPropagation();
                converter.csv({
                    name: 'pis_da_area',
                    type: context,
                    data: features
                });
            });
        }

        customScrollbar(content);
    }
}

export default GreenfieldInfo;



// WEBPACK FOOTER //
// ./src/scripts/info/GreenfieldInfo.js